// src/pages/document/DocumentCategoryListPage.tsx
// Auto-generated by corebuilder. Edit freely.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';

/**
 * Document category list page.
 */
export const DocumentCategoryListPage = observer(() => {
  const store = useDocument();
  const { t } = useI18n();
  const [categories, setCategories] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadCategories = async () => {
      setIsLoading(true);
      try {
        const response = await store.ListCategories();
        setCategories(response?.categories?.categories ?? []);
      } catch {
        // Error is handled by store
      } finally {
        setIsLoading(false);
      }
    };
    loadCategories();
  }, []);

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/documents" className="text-muted-foreground hover:text-foreground">
              {t('document.list')}
            </Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">{t('document.categories')}</span>
          </nav>
          <h1 className="text-2xl font-bold text-foreground">{t('document.categories')}</h1>
        </div>
        <Link to="/documents/categories/new">
          <Button>{t('document.createCategory')}</Button>
        </Link>
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Depth</TableHead>
              <TableHead>Sort Order</TableHead>
              <TableHead>Documents</TableHead>
              <TableHead>Created At</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : categories.length === 0 ? (
              <TableEmpty colSpan={8} message={t('document.noCategories')} />
            ) : (
              categories.map((item: any) => (
                <TableRow key={item.entity?.id || item.id || Math.random()}>
                  <TableCell>
                    <span style={{ paddingLeft: `${(item.depth ?? 0) * 16}px` }}>
                      {String(item.name ?? '')}
                    </span>
                  </TableCell>
                  <TableCell>
                    {String(item.description ?? '')}
                  </TableCell>
                  <TableCell>
                    {String(item.slug ?? '')}
                  </TableCell>
                  <TableCell>
                    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
                      {String(item.depth ?? 0)}
                    </span>
                  </TableCell>
                  <TableCell>
                    {String(item.sort_order ?? '')}
                  </TableCell>
                  <TableCell>
                    {String(item.document_count ?? 0)}
                  </TableCell>
                  <TableCell>
                    {String(item.created_at ?? '')}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Link to={`/documents/categories/${item.entity?.id || item.id}/edit`}>
                        <Button variant="ghost" size="sm">{t('common.edit')}</Button>
                      </Link>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive"
                        onClick={() => {
                          if (confirm(t('document.deleteCategoryConfirm'))) {
                            store.DeleteCategory(item.entity?.id || item.id).then(() => {
                              setCategories((prev) => prev.filter(
                                (c: any) => (c.entity?.id || c.id) !== (item.entity?.id || item.id)
                              ));
                            });
                          }
                        }}
                      >
                        {t('common.delete')}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default DocumentCategoryListPage;
